import type { ControlResultActivityListItem } from "@/models/control-result-activity.model";
import { showAlertCallout } from "@/shared/alerts";
import { ControlResultActivityFactory } from "@/shared/factories/control-result-activity.factory";
import {
  getGetControlResultAttachmentsQueryKey,
  useCreateComment,
} from "@/spz-api/control-result-comments/control-result-comments";
import {
  getGetControlResultActivitiesQueryKey,
  getGetControlResultListPaginatedQueryKey,
} from "@/spz-api/control-results/control-results";
import type { RichTextDto } from "@/spz-api/model";
import type { CreateControlResultCommentRequestDto } from "@/spz-api/model/createControlResultCommentRequestDto";
import { useGetMeSuspense } from "@/spz-api/user/user";
import type { RichText } from "@spz/shared-domain";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const useControlResultActivityCommentForm = ({
  controlId,
  controlResultId,
}: { controlId: string; controlResultId: string }) => {
  const [comment, setComment] = useState<RichText>({
    content: [],
    type: "doc",
  });
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { data: me } = useGetMeSuspense();

  // Helper: Submit comment to backend
  const submitCommentToBackend = (
    content: RichText,
    attachmentIds: string[] | undefined,
    callbacks: { onSuccess: () => void; onError?: () => void },
  ) => {
    const commentData: CreateControlResultCommentRequestDto = {
      content: content as RichTextDto,
      ...(attachmentIds && { attachmentIds }),
    };

    createCommentMutate(
      {
        controlId,
        controlResultId,
        data: commentData,
      },
      {
        onSuccess: callbacks.onSuccess,
        onError: (error: unknown) => {
          console.error("Comment submission failed:", error);
          callbacks.onError?.();
        },
      },
    );
  };

  const { mutate: createCommentMutate } = useCreateComment({
    mutation: {
      onMutate: async (variables) => {
        const { data } = variables;

        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: getGetControlResultActivitiesQueryKey(
            controlId,
            controlResultId,
          ),
        });

        // Snapshot the previous value
        const previousActivities = queryClient.getQueryData<
          ControlResultActivityListItem[]
        >(getGetControlResultActivitiesQueryKey(controlId, controlResultId));

        // Create optimistic activity item
        const optimisticActivity =
          ControlResultActivityFactory.createOptimisticComment(
            {
              id: String(me.id),
              fullName: `${me.firstName} ${me.lastName}`,
              profilePictureUrl: me.avatarUrl ? String(me.avatarUrl) : null,
            },
            data.content,
          );

        // Optimistically update the activity list
        queryClient.setQueryData<ControlResultActivityListItem[]>(
          getGetControlResultActivitiesQueryKey(controlId, controlResultId),
          (old) => {
            if (!old) return [optimisticActivity];
            return [optimisticActivity, ...old];
          },
        );

        // Return context with snapshot for rollback
        return { previousActivities };
      },
      onError: (_, __, context) => {
        // Rollback to the previous value on error
        if (context?.previousActivities) {
          queryClient.setQueryData<ControlResultActivityListItem[]>(
            getGetControlResultActivitiesQueryKey(controlId, controlResultId),
            context.previousActivities,
          );
        }

        showAlertCallout({
          type: "critical",
          title: t("error_comment_add"),
        });
      },
      onSettled: () => {
        // Always refetch after error or success to ensure consistency
        queryClient.invalidateQueries({
          queryKey: getGetControlResultActivitiesQueryKey(
            controlId,
            controlResultId,
          ),
        });
        // Refresh the controls list to update the comments count
        queryClient.invalidateQueries({
          queryKey: getGetControlResultListPaginatedQueryKey(controlId),
        });
        // Refresh attachments
        queryClient.invalidateQueries({
          queryKey: getGetControlResultAttachmentsQueryKey(
            controlId,
            controlResultId,
          ),
        });
      },
    },
  });

  const handleCommentSubmit = async ({
    onSuccess,
    onError,
    commentContent,
    fileToUpload,
    uploadFile,
  }: {
    onSuccess: () => void;
    onError?: () => void;
    commentContent?: RichText;
    fileToUpload?: File | null;
    uploadFile?: (file: File) => Promise<string | undefined>;
  }) => {
    const contentToUse = commentContent || comment;

    // Handle file upload case: upload file first, then create comment with attachment
    if (fileToUpload && uploadFile) {
      try {
        const uploadedAttachmentId = await uploadFile(fileToUpload);

        if (!uploadedAttachmentId) {
          onError?.();
          return;
        }

        submitCommentToBackend(contentToUse, [uploadedAttachmentId], {
          onSuccess,
          onError,
        });
      } catch (error) {
        console.error("File upload failed:", error);
        onError?.();
      }
    } else {
      // Normal flow: no file upload, just create comment
      submitCommentToBackend(contentToUse, undefined, { onSuccess, onError });
    }
  };

  return {
    value: comment,
    valid: comment?.content.length > 0,
    update: (_comment: RichText) => {
      setComment(_comment);
    },
    submit: handleCommentSubmit,
    reset: () => {
      setComment({ content: [], type: "doc" });
    },
  };
};
